/* eslint-disable import/no-unresolved */
/**
 * Biometric identification widget (v3) using UI kit `BiometricIdentification`.
 *
 * Behaviour matches the legacy widget (`pages/desktop/widgets/BiometricIdentification.jsx`):
 * - First checkbox ("don't show again"): on close -> `fingerprintHideWidget(!checked)` (persists `showAgain`).
 * - Second checkbox (T&C): syncs with `acceptTermsConditionsRedux` via
 *   `fingerprintSetAcceptTermsConditions` (reducer toggles; dispatch when desired value differs from store).
 */
import React, { useCallback, useEffect, useState } from "react";
import { bool, func, shape } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";

import { BiometricIdentification } from "@ui-kit/components/index.es";

import { actions as fingerprintActions, selectors as fingerprintSelectors } from "reducers/fingerprint";
import { actions as notificationPushActions } from "reducers/pushNotifications";
import { resizableRoute } from "pages/_components/Resizable";
import * as configUtil from "util/config";
import * as deviceUtils from "util/device";
import { getBiometricWidgetLabels } from "v3/v3UiKitI18n";

/**
 * Renders the biometric enrollment prompt (bottom sheet) when fingerprint login is enabled,
 * the app runs on native mobile, biometric hardware is available, and the user has not
 * already configured or dismissed the widget.
 *
 * @param {object} props
 * @param {import("redux").Dispatch} props.dispatch
 * @param {{ isAvailable?: boolean, isHardwareDetected?: boolean } | null | undefined} props.availability
 * @param {boolean | null | undefined} props.deviceWithFingerprint
 * @param {boolean} props.hidden
 * @param {boolean | null | undefined} props.isMobileNative Injected by `resizableRoute`.
 * @param {boolean} props.showAgain Persisted preference to show the widget again.
 * @param {boolean} props.acceptTermsFromStore T&C acceptance flag from the fingerprint slice.
 */
function BiometricIdentificationV3({
    dispatch,
    availability,
    deviceWithFingerprint,
    hidden,
    isMobileNative,
    showAgain,
    acceptTermsFromStore,
}) {
    const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);

    const fingerprintEnabled = configUtil.getBoolean("frontend.login.fingerprint.enabled");

    useEffect(() => {
        let device = "";
        if (isMobileNative) {
            device = deviceUtils.getDeviceId();
        }
        dispatch(fingerprintActions.fingerprintConfigurationPre(device));
        dispatch(fingerprintActions.fingerprintAvailability());
    }, [dispatch, isMobileNative]);

    const hardwareAvailability = isMobileNative && availability && availability.isHardwareDetected;
    const showWidget = fingerprintEnabled && hardwareAvailability && !deviceWithFingerprint && !hidden && showAgain;

    const handleClose = useCallback(() => {
        dispatch(fingerprintActions.fingerprintHideWidget(!dontShowAgainChecked));
        dispatch(notificationPushActions.setBiometryCancelled(true));
    }, [dispatch, dontShowAgainChecked]);

    const handleConfigure = useCallback(() => {
        handleClose();
        dispatch(push("/settings/fingerprintConfiguration"));
    }, [dispatch, handleClose]);

    const handleSecondaryCheckboxChange = useCallback(
        (checked) => {
            if (checked !== acceptTermsFromStore) {
                dispatch(fingerprintActions.fingerprintSetAcceptTermsConditions());
            }
        },
        [dispatch, acceptTermsFromStore],
    );

    if (!showWidget) {
        return null;
    }

    const labels = getBiometricWidgetLabels();
    const widgetBodyFromConfig = configUtil.get("biometric.AllowsAuthetication.Text");
    const description =
        widgetBodyFromConfig && String(widgetBodyFromConfig).trim() !== "" ? widgetBodyFromConfig : labels.body;

    return (
        <BiometricIdentification
            automationAttributes={{ "data-ui-automation": "widgetNotifications" }}
            closeLabel={labels.close}
            closeOnBackdrop={false}
            configureLabel={labels.configureNow}
            description={description}
            dontShowAgainChecked={dontShowAgainChecked}
            dontShowAgainLabel={labels.dontShowAgain}
            isVisible
            modalControlElementId=""
            onClose={handleClose}
            onConfigure={handleConfigure}
            onDontShowAgainChange={setDontShowAgainChecked}
            onSecondaryCheckboxChange={handleSecondaryCheckboxChange}
            secondaryCheckboxChecked={acceptTermsFromStore}
            secondaryCheckboxLabel={labels.secondaryAcceptTerms}
            title={labels.title}
        />
    );
}

BiometricIdentificationV3.propTypes = {
    dispatch: func.isRequired,
    availability: shape({
        isAvailable: bool,
        isHardwareDetected: bool,
    }),
    deviceWithFingerprint: bool,
    hidden: bool,
    isMobileNative: bool,
    showAgain: bool,
    acceptTermsFromStore: bool,
};

BiometricIdentificationV3.defaultProps = {
    availability: null,
    deviceWithFingerprint: null,
    hidden: true,
    isMobileNative: null,
    showAgain: true,
    acceptTermsFromStore: false,
};

/** @param {object} state Redux root state */
const mapStateToProps = (state) => ({
    availability: fingerprintSelectors.getAvailability(state),
    deviceWithFingerprint: fingerprintSelectors.getIsDeviceWithFingerprint(state),
    hidden: fingerprintSelectors.getFetching(state) || fingerprintSelectors.isHidden(state),
    showAgain: fingerprintSelectors.isShowAgain(state),
    acceptTermsFromStore: fingerprintSelectors.getAcceptTermsConditions(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(BiometricIdentificationV3);
